import { Box, Collapse, Flex, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { ToggleGroup } from '@/module/AnnualReport/components/ToggleGroup';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

interface CollapsableGroupProps<T> {
  title: string;
  items: T[];
  renderValidation: (t: T) => ReactNode;
  renderItem: (t: T) => ReactNode;
  getItemKey?: (t: T) => string | number;
}

export function CollapsableGroup<T>({
  title,
  items,
  renderItem,
  renderValidation,
  getItemKey,
}: CollapsableGroupProps<T>) {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure(true);

  return (
    <Suspense fallback={<PendingSpinner />}>
      <Box p="10px">
        <ToggleGroup
          opened={opened}
          toggle={toggle}
          title={
            <Text fw="500">
              {title} ({items.length})
            </Text>
          }
        />
        <Collapse in={opened}>
          <Stack mt="10px">
            {items.map((item, index) => (
              <Flex
                gap="7px"
                key={getItemKey?.(item) ?? index}
              >
                {renderValidation && renderValidation(item)}
                {renderItem(item)}
              </Flex>
            ))}
          </Stack>
          {items.length === 0 && (
            <NoContentPlaceholder
              mih="70px"
              title={t('noContent.title')}
            />
          )}
        </Collapse>
      </Box>
    </Suspense>
  );
}
